import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useParams, useLocation } from 'react-router-dom';
import ArticleList from './components/ArticleList';
import ArticleListDebug from './components/ArticleListDebug';
import HomePage from './components/HomePage'; // Import the new HomePage component
import RidePage from './components/RidePage';
import { logRideEvent } from './rideTimeline';

import './App.css';
import './components/Article.css';
import './components/ArticleList.css';
import './UnauthenticatedMessage.css';

// Only meaningful on the courier route (/:conneoId/rit/:rideId) - elsewhere
// there's no rideId to trace against, and logRideEvent itself is a no-op
// without one.
const rideRouteParams = () => {
  const match = window.location.pathname.match(/^\/([^/]+)\/rit\/([^/]+)/i);
  return match ? { conneoId: match[1], rideId: match[2] } : null;
};

// Without this, an uncaught render error (e.g. unexpected data from the
// backend) unmounts the whole tree and leaves a blank white screen with no
// way to recover other than clearing state - reloading re-fetches the same
// data and crashes again. Catching it here shows a message and a way out.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Onbehandelde fout in de app:', error, info);

    const params = rideRouteParams();
    if (params) {
      logRideEvent(params.conneoId, params.rideId, {
        eventName: 'AppCrash',
        eventType: 'Error',
        eventMessage: (error && error.message) || 'Onbekende fout in de app',
        functionName: 'ErrorBoundary.componentDidCatch',
        extraData: {
          error: error && error.message,
          stack: error && error.stack,
          componentStack: info && info.componentStack,
          pathname: window.location.pathname
        }
      });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="unauthenticated-container">
          <div className="unauthenticated-icon">⚠️</div>
          <h2 className="unauthenticated-title">Er is iets misgegaan</h2>
          <p className="unauthenticated-text">
            Probeer de pagina te herladen. Blijft dit gebeuren, neem dan contact op met Andi.
          </p>
          <button type="button" className="ride-btn ride-btn-primary" onClick={() => window.location.reload()}>
            Herladen
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Catches what the React error boundary structurally can't: errors thrown
// from event handlers, timers (setInterval in RidePage's retry loop) and
// unhandled promise rejections. Without this, those fail silently in the
// console with no trace anywhere.
function useGlobalErrorReporting() {
  useEffect(() => {
    const handleError = (event) => {
      const params = rideRouteParams();
      if (!params) return;
      const error = event.error;
      logRideEvent(params.conneoId, params.rideId, {
        eventName: 'WindowError',
        eventType: 'Error',
        eventMessage: (error && error.message) || event.message || 'Onbekende fout in de app',
        functionName: 'window.onerror',
        extraData: {
          message: event.message,
          error: error && error.message,
          stack: error && error.stack,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          pathname: window.location.pathname
        }
      });
    };

    const handleRejection = (event) => {
      const params = rideRouteParams();
      if (!params) return;
      const reason = event.reason;
      logRideEvent(params.conneoId, params.rideId, {
        eventName: 'UnhandledRejection',
        eventType: 'Error',
        eventMessage: (reason && reason.message) || String(reason) || 'Onbekende promise-fout',
        functionName: 'window.onunhandledrejection',
        extraData: {
          reason: reason && reason.message ? reason.message : String(reason),
          stack: reason && reason.stack,
          pathname: window.location.pathname
        }
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);
}

function App() {
  useGlobalErrorReporting();

  return (
    <Router>
      <div className="App">
        <AppHeader />
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<UnauthenticatedMessage />} />
            <Route path="/DEBUG/rit/:jsonName" element={<RidePage debug />} />
            <Route path="/DEBUG/:jsonName" element={<ArticleListDebug />} />
            <Route path="/:conneoId" element={<HomePage />} />
            <Route path="/:conneoId/rit/:rideId" element={<RidePage />} />
            <Route path="/:conneoId/:batchId" element={<ArticleListWrapper />} />
          </Routes>
        </ErrorBoundary>
      </div>
    </Router>
  );
}

// The courier ride flow renders its own header (carrier name), so the
// generic warehouse header is hidden on /rit/ routes.
function AppHeader() {
  const { pathname } = useLocation();
  const isRideRoute = pathname.toLowerCase().includes('/rit/');
  if (isRideRoute) return null;
  return (
    <header className="App-header">
      <h4>Conneo Scan {'>'} magazijn</h4>
    </header>
  );
}

function UnauthenticatedMessage() {
  document.title = `Conneo Scan > magazijn`;
  return (
    <div className="unauthenticated-container">
      <div className="unauthenticated-icon">🔒</div>
      <h2 className="unauthenticated-title">Access Denied</h2>
      <p className="unauthenticated-text">
        You are not authenticated to view this page. Please provide a valid <strong>Batch ID</strong> to proceed.
      </p>
    </div>
  );
}

function ArticleListWrapper() {
  document.title = `Conneo Scan > magazijn`;
  const { conneoId, batchId } = useParams();

  if (!batchId) {
    return <UnauthenticatedMessage />;
  }

  return <ArticleList conneoId={conneoId} batchId={batchId} />;
}

export default App;
